import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
	Typography,
	useTheme,
} from '@mui/material';
import { createNewEdition, getNewEditionToken } from '../../api/content-api';
import { loginPage } from '../../store/fixed-routing';
import { useContext, useEffect, useState } from 'react';
import AuthContext from '../../store/auth-context';
import SnackbarContext from '../../store/snackbar-context';
import { useNavigate } from 'react-router-dom';
import Olympics from '../../model/Olympics';
import OlympicsContext from '../../store/olympics-context';
import { useCustomTheme } from '../../store/theme-context';

type Props = {
	open: boolean;
	close: () => void;
	onSuccess: () => void;
};
const NewOlympicsDialog = ({ open, close, onSuccess }: Props) => {
	const { palette } = useTheme();
	const { isHighContrast } = useCustomTheme();
	const { token, logout } = useContext(AuthContext);
	const { setOlympics } = useContext(OlympicsContext);
	const [editionToken, setEditionToken] = useState<any>();
	const [confirmationToken, setConfirmationToken] = useState<string>('');
	const navigate = useNavigate();
	const { setMsg } = useContext(SnackbarContext);
	const [sendingRequest, setSendingRequest] = useState(false);

	useEffect(() => {
		if (open) {
			getNewEditionToken(token)
				.then((response) => {
					if (response.status === 200) {
						setEditionToken(response.data);
					} else {
						setMsg({ msg: response.data });
					}
				})
				.catch((exception) => {
					console.error(exception);
					if (exception.response.status === 401 || exception.response.status === 403) {
						logout();
						navigate(loginPage);
						setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
					} else {
						setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
					}
				});
		} else {
			setEditionToken(null);
			setConfirmationToken('');
		}
	}, [open]);
	const handleApprove = () => {
		if (confirmationToken !== editionToken?.ui_token) {
			setMsg({ msg: 'Niepoprawny kod', severity: 'error' });
			return;
		}
		setSendingRequest(true);
		if (sendingRequest) {
			return;
		}
		createNewEdition(token, editionToken.token)
			.then((response) => {
				if (response.status === 200) {
					setOlympics(Olympics.fromApiResponse(response.data));
					close();
					onSuccess();
					const editionLabel = response.data?.edition ? ` Edycja: ${response.data.edition}.` : '';
					setMsg({
						msg: `Utworzono nową edycję — panel przełączył się na nią.${editionLabel} Poprzednia została w bazie. Uzupełnij ustawienia (publikacje i ankieta startują odznaczone).`,
						severity: 'success',
					});
				} else {
					setMsg({ msg: response.data });
				}
				setSendingRequest(false);
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else {
					const msg = exception?.response?.data?.message
						? exception?.response?.data?.message
						: 'Błąd serwera. Spróbuj za chwilę';
					setMsg({ msg });
				}
				setSendingRequest(false);
			});
	};

	return (
		<>
			<Dialog
				open={open}
				onClose={() => close()}
			>
				<DialogTitle sx={{ fontSize: '24px' }}>Utworzyć nową edycję olimpiady?</DialogTitle>
				<DialogContent>
					<Typography sx={{ fontSize: '16px', color: isHighContrast ? palette.primary.light : 'inherit' }}>
						Poprzednia edycja <strong>nie zostanie usunięta</strong> — zostaje w bazie. Panel przełączy się na nową
						edycję (puste listy uczniów/nauczycieli, skopiowane okręgi i szkoły, publikacje i ankieta odznaczone).
					</Typography>
					<Typography sx={{ fontSize: '16px', mt: 1 }}>
						Opcjonalnie: wcześniej pobierz Excel z danymi bieżącej edycji — potem też możesz zmienić edycję w
						ustawieniach i pobrać dane historyczne.
					</Typography>
					<Typography sx={{ fontSize: '16px' }}>Ten proces może zająć dłuższą chwilę...</Typography>
					<Typography sx={{ fontSize: '20px', mt: '50px' }}>
						Przepisz kod aby kontynuować: {editionToken?.ui_token}
					</Typography>
					{editionToken && (
						<TextField
							sx={{ mt: 2 }}
							fullWidth
							variant={'standard'}
							value={confirmationToken}
							onChange={(event) => setConfirmationToken(event.target.value)}
						/>
					)}
				</DialogContent>
				<DialogActions>
					{sendingRequest && <CircularProgress size={24} />}
					<Button
						disabled={sendingRequest}
						onClick={() => close()}
					>
						Anuluj
					</Button>
					<Button
						disabled={sendingRequest}
						onClick={() => handleApprove()}
						variant='contained'
					>
						Utwórz nową edycję
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default NewOlympicsDialog;
